import {Injectable, OnDestroy} from '@angular/core';
import {Observable, BehaviorSubject, of, Subscription, delay, tap} from 'rxjs';
import {map, catchError, switchMap, finalize} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {Router} from '@angular/router';
import {UserModel} from "../../models/user.model";
import {AuthHTTPService} from "../auth-http";
import {AuthModel} from "../../models/auth.model";
import {CommonResponse} from "../../models/CommonResponse.model";

export type UserType = CommonResponse<UserModel > | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  private timer?: Subscription;
  private accessToken$ = new BehaviorSubject<string | null>(null);
  private refreshToken$ = new BehaviorSubject<string | null>(null);

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router
  ) {

    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);

  }

  // public methods
  login(email: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password)
      .pipe(
        map((auth: AuthModel) => {
          const result = this.setAuthFromLocalStorage(auth);
          
          return result;
        }),
        switchMap(() => this.getUserByToken()),
        catchError((err) => {
          console.error('err', err);
          return of(undefined);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  logout() {
    // localStorage.removeItem(this.authLocalStorageToken);
    localStorage.clear();

    this.stopTokenTimer();
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.accessToken).pipe(
      map((user: UserType) => {
       
        if (user?.data.role.type === "CLIENT" || user?.data.role.type === "SUPER_ADMIN" || user?.data.role.type === "ADMIN") {
   
          this.currentUserSubject.next(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // const allowedRoles = ['SUPER_ADMIN', 'ADMIN', 'CLIENT'];
  
  // if (user?.data?.role?.type && allowedRoles.includes(user.data.role.type)) {


  // need create new user then login
  registration(user: UserModel) {
    return null
    /*
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
     */
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    if (auth && auth.data.accessToken) {
      const fullAuth = {
        ...auth.data,
        createdAt: new Date().toISOString()  
      };
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(fullAuth));
      return true;
    }
    return false;
  }
  

  private getAuthFromLocalStorage(): any | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  refreshToken(): Observable<any | null> {
    const refreshTokenData = localStorage.getItem(this.authLocalStorageToken);
    if (!refreshTokenData) {
      localStorage.removeItem(this.authLocalStorageToken);
      return of(null);
    }
    return this.authHttpService.getRefreshToken()
      .pipe(
        map((auth: AuthModel) => {
          this.startTokenTimer();
          this.setAuthFromLocalStorage(auth);
        })
      );

  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }


  private getTokenRemainingTime() {
    const accessTokenData = localStorage.getItem(this.authLocalStorageToken);
    if (!accessTokenData) {
      return 0;
    }
    const {expiresIn} = JSON.parse(accessTokenData);
    return expiresIn * 1000;
  }

  private startTokenTimer() {
    const timeout = this.getTokenRemainingTime();
    this.timer = of(true)
      .pipe(
        delay(timeout),
        tap({
          next: () => this.refreshToken().subscribe(),
        })
      )
      .subscribe();
  }

  private stopTokenTimer() {
    this.timer?.unsubscribe();
  }

  private clearLocalStorage(): void {
    localStorage.removeItem(this.authLocalStorageToken);
  }


  isLoggedIn(): boolean {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken || !auth.expiresIn) {
      return false;
    }
  

    const expireTime = parseInt(auth.expiresIn) * 1000;
    const storedTime = new Date(auth.createdAt).getTime();
    const currentTime = Date.now();
  
    return currentTime < (storedTime + expireTime);
  }

  

}
